/**
 * The room the lightbox leaves around the image (PhotoSwipe's paddingFn;
 * concepts/frontend-design.md, "The lightbox"):
 *
 * - the top bar above the image and the caption below it, while the bars are visible;
 * - none for the bars on a screen of 520 px height or less - a phone held sideways, the
 *   breakpoint of the dialog sheet -, where the bars lie over the image, fade out after the
 *   navigation fade delay and come back with a tap: until 7.0.0 they took 28 % of the
 *   height, and a 3:2 photo was drawn 424 x 283 px on an 844 x 390 px screen (LB-3);
 * - always the safe areas of the screen (a notch, the home indicator), which a template with
 *   viewport-fit=cover hands to the page, so the image never lies under them (LB-4).
 *
 * @param {object} measures
 * @param {boolean} measures.barsVisible whether the top bar and the caption are shown
 * @param {boolean} measures.lowScreen whether the screen is 520 px high or less
 * @param {boolean} [measures.overlapToolbar] the option doOverlapToolbar
 * @param {boolean} [measures.overlapCaption] the option doOverlapImageWithCaption
 * @param {number} [measures.imagePadding] the option imagePadding
 * @param {number} [measures.topBarHeight] the height of the top bar, its safe area included
 * @param {number} [measures.captionHeight] the height of the caption of the image with its
 *                                          distance to the bottom, 0 without a caption
 * @param {{top: number, right: number, bottom: number, left: number}} [measures.safeArea]
 * @returns {{top: number, bottom: number, left: number, right: number}}
 */
export function lightboxPadding({
    barsVisible,
    lowScreen,
    overlapToolbar = false,
    overlapCaption = false,
    imagePadding = 0,
    topBarHeight = 0,
    captionHeight = 0,
    safeArea = {top: 0, right: 0, bottom: 0, left: 0},
}) {
    let top = 0;
    let bottom = 0;

    if (barsVisible && !lowScreen) {
        top = (overlapToolbar ? 0 : topBarHeight) + imagePadding;
        bottom = overlapCaption ? 0 : captionHeight;
    }

    return {
        top: Math.max(top, safeArea.top),
        bottom: Math.max(bottom, safeArea.bottom),
        left: safeArea.left,
        right: safeArea.right,
    };
}

/**
 * The safe areas of the screen, read from a probe in the lightbox whose padding is the
 * env(safe-area-inset-*) of the browser (photoswipe.less, .pswp__safe-area): a script cannot
 * read env() itself.
 *
 * @param {HTMLElement} root the root of the lightbox
 * @returns {{top: number, right: number, bottom: number, left: number}}
 */
export function safeAreaOf(root) {
    let probe = root.querySelector(':scope > .pswp__safe-area');
    if (!probe) {
        probe = document.createElement('div');
        probe.className = 'pswp__safe-area';
        probe.setAttribute('aria-hidden', 'true');
        root.appendChild(probe);
    }
    const style = getComputedStyle(probe);
    const px = (value) => {
        const number = parseFloat(value);
        return Number.isFinite(number) ? number : 0;
    };
    return {
        top: px(style.paddingTop),
        right: px(style.paddingRight),
        bottom: px(style.paddingBottom),
        left: px(style.paddingLeft),
    };
}
